/**
 * Adem — the six breathing exercises (contract §8.6).
 * Exact Dutch labels; plain Dutch explanations without medical efficacy
 * claims. Breath holds are always short (<= 4s) and explicitly optional.
 */

export type NostrilSide = 'links' | 'rechts'

export type BreathPhaseKind = 'in' | 'hold' | 'out' | 'rest'

export type BreathPhase = {
  kind: BreathPhaseKind
  seconds: number
  /** Short label shown on the circle during the phase. */
  label: string
  /** Full cue announced to assistive technology. */
  announce: string
  /** Only for the alternate-nostril exercise. */
  side?: NostrilSide
}

export type BreathingExercise = {
  id: string
  /** Exact Dutch label (contract §8.6). */
  name: string
  /** One-line description for the overview card. */
  tagline: string
  /** Plain Dutch explanation, no medical efficacy claims. */
  explanation: string
  /** Visual metaphor text shown with the breathing circle. */
  metaphor: string
  phases: BreathPhase[]
  /** Shows the left/right nostril indicator during the session. */
  alternateNostril?: boolean
}

const PHASE_IN = 'Adem in'
const PHASE_HOLD = 'Houd zachtjes vast'
const PHASE_OUT = 'Adem uit'
const PHASE_REST = 'Rust even'

export const BREATHING_EXERCISES: BreathingExercise[] = [
  {
    id: 'rustig-ademen',
    name: 'Rustig ademen',
    tagline: 'Rustig in- en uitademen in een gelijk tempo.',
    explanation:
      'Adem rustig in door je neus en rustig weer uit. De cirkel wordt groter als je inademt en kleiner als je uitademt. Volg de cirkel in een tempo dat prettig voelt.',
    metaphor: 'Denk aan een golf die rustig op en neer beweegt.',
    phases: [
      { kind: 'in', seconds: 4, label: PHASE_IN, announce: 'Adem rustig in' },
      { kind: 'out', seconds: 4, label: PHASE_OUT, announce: 'Adem rustig uit' },
    ],
  },
  {
    id: 'langer-uitademen',
    name: 'Langer uitademen',
    tagline: 'Je uitademing duurt langer dan je inademing.',
    explanation:
      'Bij deze oefening duurt je uitademing langer dan je inademing. Adem rustig in en laat de lucht daarna langzaam en zachtjes weer gaan. Forceer niets; het tempo van de cirkel is een richtlijn, geen verplichting.',
    metaphor: 'Denk aan een ballon die langzaam leegloopt.',
    phases: [
      { kind: 'in', seconds: 4, label: PHASE_IN, announce: 'Adem rustig in' },
      { kind: 'out', seconds: 6, label: PHASE_OUT, announce: 'Adem langzaam uit' },
    ],
  },
  {
    id: 'rust-na-spanning',
    name: 'Rust na spanning',
    tagline: 'Een rustig moment na een druk of spannend moment.',
    explanation:
      'Deze oefening is bedoeld voor een rustig moment na spanning. Adem in, houd je adem heel kort en zachtjes vast, en adem daarna langzaam uit. Het vasthouden is maar twee tellen en mag je altijd overslaan als dat niet prettig voelt; adem dan gewoon rustig door.',
    metaphor: 'Denk aan spanning die als stof van je schouders valt.',
    phases: [
      { kind: 'in', seconds: 4, label: PHASE_IN, announce: 'Adem rustig in' },
      {
        kind: 'hold',
        seconds: 2,
        label: PHASE_HOLD,
        announce: 'Houd zachtjes vast, of adem gewoon door',
      },
      { kind: 'out', seconds: 6, label: PHASE_OUT, announce: 'Adem langzaam uit' },
    ],
  },
  {
    id: 'om-en-om-neus',
    name: 'Om en om door je neus ademen',
    tagline: 'Afwisselend door je linker- en rechterneusgat.',
    explanation:
      'Je ademt om en om door je linker- en rechter neusgat. Het scherm laat met Links en Rechts zien door welk neusgat je in- en uitademt. Je kunt zachtjes een vinger naast je neus houden om mee te wisselen, maar dat hoeft niet; rustig volgen is ook goed.',
    metaphor: 'Denk aan een slinger die rustig van links naar rechts beweegt.',
    alternateNostril: true,
    phases: [
      {
        kind: 'in',
        seconds: 4,
        label: PHASE_IN,
        announce: 'Adem in door je linkerneusgat',
        side: 'links',
      },
      {
        kind: 'out',
        seconds: 4,
        label: PHASE_OUT,
        announce: 'Adem uit door je rechterneusgat',
        side: 'rechts',
      },
      {
        kind: 'in',
        seconds: 4,
        label: PHASE_IN,
        announce: 'Adem in door je rechterneusgat',
        side: 'rechts',
      },
      {
        kind: 'out',
        seconds: 4,
        label: PHASE_OUT,
        announce: 'Adem uit door je linkerneusgat',
        side: 'links',
      },
    ],
  },
  {
    id: 'vast-ritme',
    name: 'In een vast ritme ademen',
    tagline: 'Een gelijkmatig ritme met een korte pauze.',
    explanation:
      'Je ademt in een vast, rustig ritme: in, uit, en even niets. De korte pauze na elke uitademing geeft het ritme. Volg de cirkel en het tempo; het mag altijd langzamer als dat prettiger is.',
    metaphor: 'Denk aan een metronoom die een rustig tempo aangeeft.',
    phases: [
      { kind: 'in', seconds: 4, label: PHASE_IN, announce: 'Adem rustig in' },
      { kind: 'out', seconds: 4, label: PHASE_OUT, announce: 'Adem rustig uit' },
      { kind: 'rest', seconds: 2, label: PHASE_REST, announce: 'Rust even' },
    ],
  },
  {
    id: 'rustig-focussen',
    name: 'Rustig focussen',
    tagline: 'Leg je aandacht zachtjes op je adem.',
    explanation:
      'Leg je aandacht zachtjes op je adem en tel elke ademhaling in gedachten mee. Dwaalt je aandacht af, dan begin je gewoon weer bij één. Dat is helemaal normaal en hoort erbij.',
    metaphor: 'Denk aan een kaarsvlam die stil en rustig brandt.',
    phases: [
      { kind: 'in', seconds: 4, label: PHASE_IN, announce: 'Adem rustig in' },
      {
        kind: 'hold',
        seconds: 2,
        label: PHASE_HOLD,
        announce: 'Houd zachtjes vast, of adem gewoon door',
      },
      { kind: 'out', seconds: 4, label: PHASE_OUT, announce: 'Adem rustig uit' },
    ],
  },
]

export function getExerciseById(id: string): BreathingExercise | undefined {
  return BREATHING_EXERCISES.find((exercise) => exercise.id === id)
}

/** Canonical safety cue (spec §8.6 / §5.7). Shown on overview and session. */
export const SAFETY_CUE = 'Stop bij duizeligheid of ongemak. Adem nooit geforceerd.'
